type WindowWithRestartSeq = typeof window & {
    platformRestartSeq?: number;
    platformPublishedSeq?: number;
    platformAuthoritativePublishedSeq?: number;
};

/**
 * Restart callers overlap (cold-start completion, player recovery, sign-in), and each publishes on
 * arrival — so an older response can land last and install a stale token. Tickets are taken on
 * `window` because the callers are separately bundled MFEs.
 *
 * `platformRestartSeq` is the monotonic ticket issued at the START of each restart (start-order).
 * `platformPublishedSeq` tracks the ticket of the most recent operation that actually PUBLISHED a
 * token — a restart that fails or returns no JWT never advances it, so it can't supersede an
 * earlier in-flight restart that does publish.
 */
export function takeRestartTicket(): number {
    if (typeof window === 'undefined') return 0;

    const windowWithSeq = window as WindowWithRestartSeq;
    windowWithSeq.platformRestartSeq = (windowWithSeq.platformRestartSeq ?? 0) + 1;
    return windowWithSeq.platformRestartSeq;
}

/**
 * True when no later-started operation has already published a token — i.e. publishing this ticket
 * won't install something older than what's on the window. Pure check; call `markRestartPublished`
 * on an actual publish. Gating on the PUBLISHED sequence (not the issued one) means a later restart
 * that fails or returns no JWT does not block an earlier restart's valid publish.
 */
export function isNewestRestartTicket(ticket: number): boolean {
    if (typeof window === 'undefined') return true;

    return ticket > ((window as WindowWithRestartSeq).platformPublishedSeq ?? 0);
}

/**
 * Records that this ticket's token was published, so earlier-started (lower-ticket) responses that
 * land later — including a failed later restart's earlier peer — can no longer overwrite it.
 */
export function markRestartPublished(ticket: number): void {
    if (typeof window === 'undefined') return;

    const windowWithSeq = window as WindowWithRestartSeq;
    if (ticket > (windowWithSeq.platformPublishedSeq ?? 0)) {
        windowWithSeq.platformPublishedSeq = ticket;
    }
}

/**
 * True when this ticket is not older than the newest authoritative publish — orders authoritative
 * publishes by start ticket so a stale recovery can't overwrite a newer sign-out/sign-in.
 */
export function isNewestAuthoritativePublish(ticket: number): boolean {
    if (typeof window === 'undefined') return true;

    return ticket >= ((window as WindowWithRestartSeq).platformAuthoritativePublishedSeq ?? 0);
}

/** Records the newest authoritative publish so an earlier one landing later can't overwrite it. */
export function markAuthoritativePublished(ticket: number): void {
    if (typeof window === 'undefined') return;

    const windowWithSeq = window as WindowWithRestartSeq;
    if (ticket > (windowWithSeq.platformAuthoritativePublishedSeq ?? 0)) {
        windowWithSeq.platformAuthoritativePublishedSeq = ticket;
    }
}
